import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fehlenderAenderungstext } from "./reviewRules";
import { doc, getDoc } from "firebase/firestore";
import { db, app } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { SHOP } from "../shop";

const functions = getFunctions(app, "europe-west1");
const submitOrderReview = httpsCallable(functions, "submitOrderReview");

// ── Default text ──────────────────────────────────────────────────────────────

export const ORDER_REVIEW_TEXT_DEFAULTS = {
  pageTitle: "BESTELLDETAILS BESTÄTIGEN",
  confirmMainText:
    "Ich bestätige, dass alle Bestelldetails korrekt sind und die " +
    "Produktion gestartet werden kann.",
  confirmBullets: [
    "Lieferdatum und Adresse sind korrekt.",
    "Alle Personalisierungen sind vollständig und richtig.",
    "Die Bestätigung ist verbindlich.",
  ],
  modificationLabel: "Ich habe eine Änderung oder Frage",
  modificationSubtext:
    "Bitte beschreibe unten, was geändert werden soll. " +
    "Wir melden uns schnellstmöglich bei dir.",
  feedbackLabel: "Deine Anmerkung:",
  feedbackPlaceholder:
    "z. B. Adresse korrigieren, Personalisierung anpassen…",
  // Erscheint, wenn „Änderung oder Frage" gewählt ist und das Feld leer
  // bleibt. Ohne diesen Hinweis liess sich der Änderungswunsch leer
  // abschicken — siehe Bestellung #4189459291 vom 02.10.2026: Entscheidung
  // „modification", Kommentar null, null Zeichen. Für uns nicht bearbeitbar,
  // und die Kundin wartet auf eine Antwort, die niemand geben kann.
  feedbackRequiredHint:
    "Bitte beschreibe kurz, was geändert werden soll — sonst wissen wir " +
    "nicht, worum es geht. Ist alles korrekt? Dann wähle oben " +
    "„Alles korrekt“.",
  submitButtonText: "Senden",
};

// ── Colour tokens ─────────────────────────────────────────────────────────────

const C = {
  pageBg:        SHOP.pageBg,
  card:          "#ffffff",
  primaryDark:   SHOP.primary,
  secondaryText: "#8a6f6a",
  border:        SHOP.border,
  selectedBg:    SHOP.pageBg,
  selectedBorder:SHOP.accentSoft,
  bulletText:    "#7a5c56",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}. ${["Jan","Feb","Mär","Apr","Mai","Jun",
    "Jul","Aug","Sep","Okt","Nov","Dez"][parseInt(m, 10) - 1]} ${y}`;
};

// ── Component ─────────────────────────────────────────────────────────────────

const OrderReviewForm = ({
  previewMode = false,
  sampleData  = {},
  textConfig  = null,
}) => {
  const { orderId } = useParams();
  const navigate    = useNavigate();

  const [order, setOrder]                   = useState(null);
  const [resolvedOrderId, setResolvedOrderId] = useState(null);
  const [orderExists, setOrderExists]       = useState(true);
  const [texts, setTexts]               = useState(ORDER_REVIEW_TEXT_DEFAULTS);
  const [emailCfg, setEmailCfg]         = useState({});
  const [decision, setDecision]         = useState("confirmed");
  const [feedback, setFeedback]         = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (previewMode) {
      setOrder(sampleData.order || null);
      setTexts({ ...ORDER_REVIEW_TEXT_DEFAULTS, ...(textConfig || {}) });
      return;
    }
    const fetchData = async () => {
      // 1. Try direct lookup (backward compat: old links use orderId as param)
      let orderSnap = await getDoc(doc(db, "etsy_orders", orderId));
      let realOrderId = orderId;

      if (!orderSnap.exists()) {
        // 2. New token-based link: resolve uuid → real orderId
        const tokenSnap = await getDoc(doc(db, "order_tokens", orderId));
        if (tokenSnap.exists()) {
          realOrderId = tokenSnap.data().orderId;
          orderSnap = await getDoc(doc(db, "etsy_orders", realOrderId));
        }
      }

      const [configSnap, emailCfgSnap] = await Promise.all([
        getDoc(doc(db, "config", "orderReviewPage")),
        getDoc(doc(db, "config", "orderEmailTexts")),
      ]);

      if (orderSnap.exists()) {
        const data = orderSnap.data();
        setResolvedOrderId(realOrderId);
        if (data.orderReviewedAt) {
          setOrderExists(false);
        } else {
          setOrder({ id: orderSnap.id, ...data });
        }
      } else {
        setOrderExists(false);
      }
      if (configSnap.exists()) {
        setTexts({ ...ORDER_REVIEW_TEXT_DEFAULTS, ...configSnap.data() });
      }
      if (emailCfgSnap.exists()) setEmailCfg(emailCfgSnap.data());
    };
    fetchData();
  }, [orderId, previewMode, sampleData, textConfig]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (previewMode) return;
    if (fehlenderAenderungstext(decision, feedback)) return;   // doppelt hält
    setIsSubmitting(true);
    try {
      await submitOrderReview({ orderId: resolvedOrderId || orderId, orderDecision: decision, orderFeedback: feedback });
      navigate("/order-thank-you");
    } catch (error) {
      console.error("Error submitting order review:", error);
      alert("Etwas ist schiefgelaufen. Bitte versuche es später erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!orderExists) {
    return (
      <div style={{ minHeight: "100vh", background: C.pageBg,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "2em" }}>
        <div style={{ textAlign: "center" }}>
          <img src={SHOP.logo} alt={SHOP.name}
            style={{ height: 52, marginBottom: "1.5em" }} />
          <h2 style={{ color: C.primaryDark }}>Bestellung nicht verfügbar</h2>
          <p style={{ color: C.secondaryText }}>
            Diese Bestellbestätigung ist nicht mehr verfügbar oder wurde
            bereits eingereicht.
          </p>
        </div>
      </div>
    );
  }

  // Use sample order in preview mode if no real order loaded yet
  const displayOrder = order || (previewMode ? sampleData.order : null);
  const addr  = displayOrder?.address || {};
  const items = displayOrder?.items   || [];

  const isConfirmed    = decision === "confirmed";
  const isModification = decision === "modification";
  // Ein Änderungswunsch ohne Text ist für uns wertlos und für die Kundin
  // eine Sackgasse: sie wartet auf eine Rückmeldung zu etwas, das nirgends
  // steht. Deshalb gesperrt, bis etwas drinsteht.
  const fehlenderText = fehlenderAenderungstext(decision, feedback);

  const warningText = emailCfg.eventDateWarningText ||
    "Bitte prüfen: das Datum der Personalisierung, liegt vor der Übergabe an DHL";
  const showEventDateWarning =
    displayOrder?.isDringend === true ||
    (displayOrder?.eventDateIsFuture === true &&
     !!displayOrder?.shipByDate &&
     !!displayOrder?.personalizationDate &&
     displayOrder.shipByDate >= displayOrder.personalizationDate);

  return (
    <div style={{ minHeight: "100vh", background: C.pageBg,
      padding: "2.5em 1em 4em" }}>
      <div style={{
        maxWidth: 600, margin: "0 auto",
        background: C.card, borderRadius: "16px",
        boxShadow: "0 4px 24px rgba(74,44,42,0.10)",
        padding: "2.5em 2em",
      }}>

        {/* Logo */}
        <img src={SHOP.logo} alt={SHOP.name}
          style={{ height: 84, display: "block", margin: "0 auto 0.75em" }} />

        {/* Page title */}
        <div style={{
          textAlign: "center", textTransform: "uppercase",
          letterSpacing: "0.22em", color: C.secondaryText,
          fontSize: "0.78rem", fontWeight: "600", marginBottom: "1.75em",
        }}>
          {texts.pageTitle}
        </div>

        {/* Order info card */}
        <div style={{
          background: SHOP.pageBg, borderRadius: "10px",
          border: `1px solid ${C.border}`, padding: "1.25em 1.5em",
          marginBottom: "2em", fontSize: "0.9rem",
        }}>
          {/* Ship-by date */}
          {displayOrder?.shipByDate && (
            <div style={{ marginBottom: "1em" }}>
              <span style={{ fontSize: "1.1em", marginRight: "0.4em" }}>📦</span>
              <strong style={{ color: C.primaryDark }}>Lieferzeit:</strong>
              {" "}
              <span style={{ color: "#2d3748" }}>
                Übergabe an DHL bis zum {formatDate(displayOrder.shipByDate)}
              </span>
            </div>
          )}

          {/* Warning: event date falls before DHL handover */}
          {showEventDateWarning && (
            <div style={{
              marginBottom: "1em", padding: "0.6em 0.85em",
              background: "#fffbeb", border: "1px solid #fcd34d",
              borderRadius: "6px", fontSize: "0.83rem", color: "#92400e",
              display: "flex", alignItems: "flex-start", gap: "0.45em",
            }}>
              <span style={{ flexShrink: 0 }}>⚠️</span>
              <span>{warningText}</span>
            </div>
          )}

          {/* Address */}
          {addr.raw && (
            <div style={{ marginBottom: "1em" }}>
              <div style={{ marginBottom: "0.3em" }}>
                <span style={{ fontSize: "1.1em", marginRight: "0.4em" }}>🏠</span>
                <strong style={{ color: C.primaryDark }}>Adresse:</strong>
              </div>
              <pre style={{
                margin: "0 0 0 1.8em",
                fontFamily: "inherit", fontSize: "0.875rem",
                color: "#2d3748", whiteSpace: "pre-wrap", lineHeight: "1.6",
              }}>
                {addr.raw}
              </pre>
            </div>
          )}

          {/* Items with personalization */}
          {items.length > 0 && (
            <div>
              <div style={{ marginBottom: "0.5em" }}>
                <span style={{ fontSize: "1.1em", marginRight: "0.4em" }}>✍️</span>
                <strong style={{ color: C.primaryDark }}>Personalisierung:</strong>
              </div>
              <div style={{ marginLeft: "1.8em" }}>
                {items.map((item, i) => (
                  <div key={i} style={{ marginBottom: "0.85em" }}>
                    <div style={{
                      fontSize: "0.82rem", color: "#4a5568",
                      marginBottom: "0.25em",
                    }}>
                      <strong>{i + 1})</strong> {item.title}
                      {item.variant && (
                        <span style={{ color: C.bulletText }}>
                          {" "}– {item.variant}
                        </span>
                      )}
                      {item.quantity > 1 && (
                        <span style={{ color: C.bulletText }}>
                          {" "}× {item.quantity}
                        </span>
                      )}
                    </div>
                    {/* Multi-Field Personalisierung (Etsy Multi-Personalization,
                        GA seit Mai 2026) — jedes seller-benannte Feld bekommt
                        eigenes Label. Fallback auf altes String-Feld für Orders
                        die vor dem Umbau angelegt wurden. */}
                    {(() => {
                      const personFields =
                        Array.isArray(item.personalizations) &&
                        item.personalizations.length > 0
                          ? item.personalizations
                          : (item.personalization
                              ? [{
                                  label: "Personalisierung",
                                  value: item.personalization,
                                  type: "text",
                                }]
                              : []);
                      if (personFields.length === 0) return null;
                      return (
                        <div style={{
                          background: "#fff",
                          borderLeft: `3px solid ${C.selectedBorder}`,
                          padding: "0.5em 0.75em",
                          fontSize: "0.82rem", color: "#2d3748",
                          lineHeight: "1.5",
                        }}>
                          {personFields.map((p, pi) => (
                            <div
                              key={pi}
                              style={{
                                marginBottom: pi < personFields.length - 1
                                  ? "0.6em"
                                  : 0,
                              }}
                            >
                              <div style={{
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                color: "#5a4a44",
                                textTransform: "none",
                                marginBottom: "0.15em",
                              }}>
                                {p.label || "Personalisierung"}:
                              </div>
                              <div style={{ whiteSpace: "pre-wrap" }}>
                                {p.type === "file"
                                  ? (
                                    <a
                                      href={p.value}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: C.primaryDark,
                                        textDecoration: "underline",
                                      }}
                                    >
                                      📎 Datei ansehen
                                    </a>
                                  )
                                  : p.value}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Placeholder shown in preview mode with no sample order */}
          {!displayOrder && previewMode && (
            <div style={{ color: C.secondaryText, fontStyle: "italic",
              textAlign: "center", padding: "1em 0" }}>
              Bestelldetails werden hier angezeigt
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>

          {/* Confirm option */}
          <label style={{
            display: "flex", alignItems: "flex-start", gap: "0.75em",
            padding: "1.1em", marginBottom: "0.75em",
            border: `1.5px solid ${isConfirmed ? C.selectedBorder : C.border}`,
            borderRadius: "10px", cursor: "pointer",
            background: isConfirmed ? C.selectedBg : C.card,
            transition: "border-color 0.15s, background 0.15s",
          }}>
            <input
              type="radio"
              name="decision"
              value="confirmed"
              checked={isConfirmed}
              onChange={(e) => setDecision(e.target.value)}
              style={{ marginTop: "3px", accentColor: C.primaryDark,
                flexShrink: 0 }}
            />
            <div>
              <div style={{ fontWeight: "600", color: C.primaryDark,
                fontSize: "0.9rem", lineHeight: "1.2", marginBottom: "0.65em" }}>
                {texts.confirmMainText}
              </div>
              <ul style={{ margin: 0, paddingLeft: "1.2em",
                color: C.bulletText, fontSize: "0.8rem", lineHeight: "1.15" }}>
                {(texts.confirmBullets || []).map((b, i) => (
                  <li key={i} style={{ marginBottom: "0.35em" }}>{b}</li>
                ))}
              </ul>
            </div>
          </label>

          {/* Modification option */}
          <label style={{
            display: "flex", alignItems: "flex-start", gap: "0.75em",
            padding: "1.1em", marginBottom: "1.25em",
            border: `1.5px solid ${isModification ? C.selectedBorder : C.border}`,
            borderRadius: "10px", cursor: "pointer",
            background: isModification ? C.selectedBg : C.card,
            transition: "border-color 0.15s, background 0.15s",
          }}>
            <input
              type="radio"
              name="decision"
              value="modification"
              checked={isModification}
              onChange={(e) => setDecision(e.target.value)}
              style={{ marginTop: "3px", accentColor: C.primaryDark,
                flexShrink: 0 }}
            />
            <div>
              <div style={{ fontWeight: "600", color: C.primaryDark,
                fontSize: "0.9rem", lineHeight: "1.2", marginBottom: "0.4em" }}>
                {texts.modificationLabel}
              </div>
              <div style={{ color: C.bulletText, fontSize: "0.8rem",
                lineHeight: "1.2" }}>
                {texts.modificationSubtext}
              </div>
            </div>
          </label>

          {/* Feedback textarea */}
          {isModification && (
            <div style={{ marginBottom: "1.25em" }}>
              <label style={{
                display: "block", fontWeight: "600", fontSize: "0.85rem",
                color: C.primaryDark, marginBottom: "0.5em",
              }}>
                {texts.feedbackLabel}
              </label>
              <textarea
                placeholder={texts.feedbackPlaceholder}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows="5"
                style={{
                  width: "100%", padding: "0.75em",
                  border: `1.5px solid ${C.border}`, borderRadius: "8px",
                  fontSize: "0.875rem", boxSizing: "border-box",
                  resize: "vertical", color: C.primaryDark,
                  background: SHOP.pageBg, fontFamily: "inherit",
                }}
              />
              {fehlenderText && (
                <p style={{
                  margin: "0.5em 0 0", fontSize: "0.8rem",
                  color: C.secondaryText, lineHeight: 1.45,
                }}>
                  {texts.feedbackRequiredHint}
                </p>
              )}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting || previewMode || fehlenderText}
            style={{
              width: "100%", padding: "0.8em",
              background: (isSubmitting || previewMode || fehlenderText)
                ? SHOP.accent : C.primaryDark,
              color: "#fff", border: "none", borderRadius: "8px",
              fontSize: "1rem", fontWeight: "700", letterSpacing: "0.05em",
              cursor: (isSubmitting || previewMode || fehlenderText)
                ? "not-allowed" : "pointer",
              transition: "background 0.15s",
            }}
          >
            {isSubmitting ? "Wird gesendet…" : texts.submitButtonText}
          </button>

        </form>
      </div>
    </div>
  );
};

export default OrderReviewForm;
